import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '~/app/store';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import useToasts from '~/common/components/toasts/useToasts';
import type {
  PeripheralListItem,
  PeripheralType,
} from '~/common/models/peripheral.model';

import {
  addActivationBatch,
  createActivationBatch,
  markActivationCompleted,
} from './state/activationProgressSlice';
import { selectActiveActivationBatches } from './state/activationProgressSlice.selectors';
import { useRetryActivatePeripherals } from './useRetryActivatePeripherals';

type UseRetryPeripheralOptions = {
  peripheralType: PeripheralType;
  organizationId: string | undefined;
  errorToastKey: string;
};

type UseRetryPeripheralReturn = {
  handleRetry: (item: PeripheralListItem) => Promise<void>;
  retryingPeripheralIds: Set<string>;
};

/**
 * Row-level retry-activation flow for the peripheral device lists:
 * - Calls the shared {@link useRetryActivatePeripherals} core (multiple rows
 *   can be retried concurrently)
 * - Registers each resulting batch with the global activation-progress poller
 * - Keeps each retry button busy independently until its batch reaches a
 *   terminal state (i.e. `ActivationBatchPoller` removes it from the store)
 */
const useRetryPeripheral = ({
  peripheralType,
  organizationId,
  errorToastKey,
}: UseRetryPeripheralOptions): UseRetryPeripheralReturn => {
  const dispatch = useAppDispatch();
  const { showErrorToast } = useToasts();
  const showErrorToastRef = useRef(showErrorToast);
  useEffect(() => {
    showErrorToastRef.current = showErrorToast;
  }, [showErrorToast]);
  const { submit } = useRetryActivatePeripherals();

  // peripheralId → batchId for every in-flight retry that received a batchId.
  // When a batchId is absent (API returned no batch), the peripheral is still
  // tracked here with an empty-string sentinel so we know it's retrying.
  const [activeBatches, setActiveBatches] = useState<Map<string, string>>(
    new Map()
  );

  const batches = useSelector(selectActiveActivationBatches);

  // Derive the set of currently-retrying peripheral IDs from activeBatches.
  // useMemo gives a new Set reference only when activeBatches changes, so
  // consumers' useMemo/useEffect deps invalidate on retry start/finish but
  // stay stable across unrelated renders.
  const retryingPeripheralIds = useMemo(
    () => new Set(activeBatches.keys()),
    [activeBatches]
  );

  // Clear each peripheral's busy state once the poller removes its batch.
  // Only consider entries with a real batchId (non-empty sentinel); the ''
  // sentinel means the mutation is still in-flight and hasn't returned a
  // batchId yet — those must stay until the finally block removes them.
  useEffect(() => {
    if (activeBatches.size === 0) return;
    const completed = [...activeBatches.entries()].filter(
      ([, batchId]) =>
        batchId !== '' && !batches.some((b) => b.batchId === batchId)
    );
    if (completed.length === 0) return;
    setActiveBatches((prev) => {
      const next = new Map(prev);
      for (const [peripheralId] of completed) next.delete(peripheralId);
      return next;
    });
  }, [batches, activeBatches]);

  const handleRetry = useCallback(
    async (item: PeripheralListItem) => {
      const orgId = item.organizationId ?? organizationId;
      if (!item.id || !orgId) return;
      // Capture the narrowed id so the nested state updaters below keep the
      // non-null type without repeating the guard.
      const itemId = item.id;
      // Mark this peripheral as retrying immediately (sentinel value until we
      // have a real batchId or the mutation settles without one).
      setActiveBatches((prev) => new Map(prev).set(itemId, ''));
      let batchDispatched = false;
      try {
        const outcome = await submit({
          orgId,
          fleetId: item.fleetId,
          peripheralIds: [item.id],
          peripheralType,
        });
        if (outcome.status === 'error') {
          showErrorToastRef.current(translateText(errorToastKey));
          return;
        }
        if (outcome.batchId && outcome.orgId) {
          const resolvedBatchId = outcome.batchId;
          dispatch(
            addActivationBatch(
              createActivationBatch({
                batchId: resolvedBatchId,
                orgId: outcome.orgId,
                peripheralType,
                fleetId: item.fleetId,
                peripheralIds: [itemId],
                total: 1,
              })
            )
          );
          // Replace the sentinel with the real batchId so the effect above can
          // clear this peripheral once the poller removes the batch.
          setActiveBatches((prev) =>
            new Map(prev).set(itemId, resolvedBatchId)
          );
          batchDispatched = true;
        } else {
          // API succeeded but returned no batchId (result is optional per spec).
          // Since invalidatesTags is suppressed on this mutation, trigger the
          // list refresh manually so the table doesn't stay stale.
          dispatch(markActivationCompleted());
        }
      } finally {
        // If no batch was registered (mutation failed or returned no batchId),
        // clear this peripheral's busy state immediately.
        if (!batchDispatched) {
          setActiveBatches((prev) => {
            const next = new Map(prev);
            next.delete(itemId);
            return next;
          });
        }
      }
    },
    [submit, peripheralType, organizationId, errorToastKey, dispatch]
  );

  return { handleRetry, retryingPeripheralIds };
};

export default useRetryPeripheral;
